import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import api from "../api";
import Seo from "../components/Seo";
import { IMAGES, SITE } from "../siteConfig";
import { Reveal, SectionHead } from "../components/Reveal";

export default function Insights() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/news").then((r) => { setItems(r.data.items); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  return (
    <div data-testid="insights-page" className="pt-[72px]">
      <Seo
        title="Tyre & Mining Insights | Mangalam Group"
        description="Articles and updates from Mangalam Group on mining, tyres, fleet management, technology and industry news."
        path="/insights"
      />
      <section className="py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHead
            eyebrow="News & Insights"
            title="Insights from the Field"
            copy="Notes on mining, tyres, fleet management and the technology behind tyre performance."
          />
          {loading && <p className="mt-12 font-mono text-xs tracking-[0.2em] text-slate-400 uppercase">Loading…</p>}
          <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {items.map((n, i) => (
              <Reveal key={n.id} delay={(i % 3) * 0.08}>
                <Link to={`/insights/${n.id}`} data-testid={`insight-card-${n.id}`} className="group flex h-full flex-col border border-white/10 bg-obsidian-2">
                  <div className="h-48 overflow-hidden">
                    <img src={n.image || IMAGES.tread} alt={n.title} className="h-full w-full object-cover img-industrial transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <p className="font-mono text-[9px] tracking-[0.25em] uppercase text-ember">{n.category}</p>
                    <h2 className="mt-2 font-condensed text-2xl font-bold uppercase leading-snug tracking-wide text-white">{n.title}</h2>
                    <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-400 line-clamp-3">{n.excerpt}</p>
                    <p className="mt-4 font-mono text-[9px] tracking-[0.2em] uppercase text-slate-300">
                      {n.published_at ? new Date(n.published_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : ""}
                    </p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

export function InsightDetail() {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    api.get(`/news/${id}`).then((r) => setArticle(r.data)).catch(() => setMissing(true));
  }, [id]);

  if (missing) {
    return (
      <div className="pt-[72px] py-32 text-center" data-testid="insight-missing">
        <Seo title="Article Not Found | Mangalam Group" noindex />
        <p className="font-condensed text-3xl font-bold uppercase text-white">Article not found</p>
        <Link to="/insights" className="mt-4 inline-block font-mono text-[11px] tracking-[0.2em] text-ember uppercase">Back to insights</Link>
      </div>
    );
  }
  if (!article) return <div className="pt-[72px] py-32" />;

  const articleUrl = new URL(`/insights/${article.id}`, SITE.url).toString();
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    datePublished: article.published_at,
    author: { "@type": "Organization", name: SITE.name, url: SITE.url },
    publisher: {
      "@type": "Organization",
      name: SITE.name,
      logo: { "@type": "ImageObject", url: `${SITE.url}/assets/brand/mangalam-logo.png` },
    },
    mainEntityOfPage: articleUrl,
  };

  return (
    <div data-testid="insight-detail-page" className="pt-[72px]">
      <Seo
        title={`${article.title} | Mangalam Group Insights`}
        description={article.excerpt}
        path={`/insights/${article.id}`}
        type="article"
        jsonLd={articleSchema}
      />
      <article className="mx-auto max-w-3xl px-4 sm:px-6 py-16 lg:py-24">
        <Link to="/insights" data-testid="insight-back" className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] uppercase text-slate-400 hover:text-ember transition-colors">
          <ArrowLeft className="h-3.5 w-3.5" /> All insights
        </Link>
        <p className="mt-8 font-mono text-[10px] tracking-[0.28em] uppercase text-ember">{article.category}</p>
        <h1 className="mt-4 font-condensed text-4xl sm:text-5xl font-bold uppercase leading-[1.02] tracking-tight text-white">
          {article.title}
        </h1>
        {article.image && (
          <div className="mt-10 overflow-hidden border border-white/10">
            <img src={article.image} alt={article.title} className="w-full object-cover img-industrial" />
          </div>
        )}
        <div className="mt-10 space-y-5">
          {(article.body || "").split("\n").filter(Boolean).map((p, i) => (
            <p key={i} className="text-base sm:text-lg leading-relaxed text-slate-300">{p}</p>
          ))}
        </div>
      </article>
    </div>
  );
}
